import { Injectable, WritableSignal, effect, inject, signal } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { IStoreDataV3 } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { StoreService } from '@df/catalog/store/store.service';
import { Status } from '@df/core/status';
import { Customer } from '@df/session/api/customer';
import { CustomerModel } from '@df/session/api/customer.model';
import { bufferPromise } from '@df/util/fn/buffer-promise.fn';
import { sortBy, uniq } from 'lodash-es';
import { Subscription } from 'rxjs';
import { LocalStore } from '../catalog/store/store.decorator';
import { RequestQueue } from '../util/request-queue';
declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            active_branch_number?: number;
        }
    }
}

declare module '@df/catalog/store/store' {
    interface IStoreDataV3 {
        branch_number?: number;
    }
}

export interface IBranchResponse {
    branchNumber?: string;
    result?: boolean;
    message?: string;
}

export type TProductState = Record<number, number>;
type TMultiSkuResult = Record<string, TProductState>;

@Injectable({
    providedIn: 'root'
})
export class BranchService {
    @LocalStorage('branchNumber')
    private branchNumberStorage?: number;

    private requestService = inject(RequestService);
    private storeResource = inject(StoreResource);
    private requestQueue = inject(RequestQueue);
    private finderService = inject(StoreService);
    private customer = inject(Customer);
    private customerSubscription: Subscription | undefined;

    readonly branchNumber: WritableSignal<number | undefined>;

    /**
     * Stock states per key
     * key is `${productId}.${branchId}`
     * value is stock status
     */
    private productState = new Map<string, TProductState>();

    /**
     * All stores used as branches
     */
    private branches?: Map<number, LocalStore<IStoreDataV3>>;

    /**
     * Buffer for branches ids
     */
    private allBranchesIds?: number[];

    constructor() {
        const branchNumber = this.branchNumberStorage;
        this.branchNumber = signal<number | undefined>(typeof branchNumber === 'number' && branchNumber > 0 ? branchNumber : undefined);

        this.customerUpdate();
        effect(() => {
            const branchNumber = this.branchNumber();
            if (branchNumber !== this.branchNumberStorage) {
                this.branchNumberStorage = branchNumber;
            }
        });
    }

    ngOnDestroy(): void {
        this.customerSubscription?.unsubscribe();
    }

    private setBranchNumber(branchNumber: number | undefined): void {
        this.branchNumber.set(branchNumber);
        this.productState.clear();
    }

    async updateBranch(
        branchNumber: number,
        onSuccess?: (response: IBranchResponse) => void,
        onError?: (response: IBranchResponse) => void,
        status?: Status
    ): Promise<IBranchResponse> {
        const response = await this.requestService.send(status, {
            path: '/cart/updatecollectionbranch',
            data: {
                Collection: branchNumber.toString()
            }
        });
        if (response?.result) {
            this.setBranchNumber(branchNumber);
            if (onSuccess) {
                onSuccess(response);
            }
        } else {
            if (status) {
                status.error = response?.message;
            }

            if (onError) {
                onError(response);
            }
        }

        return response;
    }

    private async customerUpdate() {
        const model = await this.customer.model.load();
        this.onCustomerUpdate(model);

        // check postoce on customer data change
        this.customerSubscription = this.customer.model.obs.data.subscribe(customerModel => {
            this.onCustomerUpdate(customerModel);
        });
    }

    private async onCustomerUpdate(customerModel: CustomerModel) {
        // check if branch exists
        const activeCustomerBranch =
            customerModel.data.active_branch_number && (await this.getAllBranchesIds()).includes(customerModel.data.active_branch_number)
                ? customerModel.data.active_branch_number
                : undefined;

        if (!activeCustomerBranch && this.branchNumberStorage) {
            this.updateBranch(this.branchNumberStorage);
        } else if (activeCustomerBranch) {
            this.setBranchNumber(customerModel.data.active_branch_number);
        }
    }

    /**
     * Checks if product is availble for collection
     */
    isAvailbleForCollection(product: Product<IProductDataV2>): boolean {
        return !!product.data?.is_collection;
    }

    /**
     * Retrieves product stock availability in a single branch
     */
    async retrieve(sku: string, branch: number): Promise<number | undefined> {
        const state = await this.retrieveInAll(sku);
        return state?.[branch] || 0;
    }

    /**
     * Loads product information about stock availability in branches
     */
    async retrieveInAll(sku: string): Promise<TProductState | undefined> {
        if (!this.productState.has(sku)) {
            const result = await this.requestQueue.add<TMultiSkuResult>(async () => {
                const r = this.productState.get(sku);
                if (r) {
                    return { sku: r };
                }

                const res = await this.requestService.sendXHR<TMultiSkuResult>(
                    'stock/by-branch',
                    { sku: [sku] },
                    { method: 'GET', api: 'node' }
                );

                return res;
            });
            for (const sku in result) {
                this.productState.set(sku, result[sku]);
            }
        }

        return this.productState.get(sku);
    }

    get branch(): LocalStore<IStoreDataV3> | undefined {
        const id = this.branchNumber();
        return id ? this.branches?.get(id) : undefined;
    }

    /**
     * Takes store name by branch id
     *
     * IMPORTANT - you need to load branches befor using it
     */
    toName(branchId: number): string {
        return this.branches?.get(branchId)?.data?.['store_name'] || '';
    }

    /**
     * Takes branch id from store
     */
    toBranchId(store: LocalStore<IStoreDataV3>): number | undefined {
        return store.data?.branch_number;
    }

    /**
     * Takes branch by store id
     */
    getBranch(branchId: number): LocalStore<IStoreDataV3> | undefined {
        return this.branches?.get(branchId);
    }

    /**
     * Loads all branches ids if needed
     */
    async load(): Promise<boolean> {
        if (this.allBranchesIds) {
            return false;
        }
        await this.getAllBranchesIds();
        return true;
    }

    /**
     * Gets sorted branhces ids (loads stores first)
     */
    private async getAllBranchesIds(): Promise<number[]> {
        if (!this.allBranchesIds) {
            this.allBranchesIds = Array.from((await bufferPromise(this, () => this.loadBranches())).keys())
                .map(Number)
                .sort();
        }
        return this.allBranchesIds!;
    }

    /**
     * Loads all stores used as branches
     */
    private async loadBranches(): Promise<Map<number, LocalStore<IStoreDataV3>>> {
        if (!this.branches) {
            const map = new Map<number, LocalStore<IStoreDataV3>>();
            (await this.storeResource.loadAll(3)).forEach(store => {
                const branchId = this.toBranchId(store as LocalStore);
                if (branchId) {
                    map.set(branchId, store as LocalStore);
                }
            });
            this.branches = map;
        }
        return this.branches;
    }

    async pickNearByBranch(postcode: string): Promise<void> {
        const findData = {
            address: postcode,
            verbosity: 3
        };

        await this.finderService.locate(undefined, findData, (response: LocalStore<IStoreDataV3>[]) => {
            const stores = sortBy(
                uniq(response.filter(findableModel => findableModel.data && findableModel.data.latitude && findableModel.data.longitude)),
                'distance'
            );
            if (stores?.[0]?.data?.branch_number) {
                this.updateBranch(stores[0].data.branch_number, () => {}, undefined);
            }
        });
    }
}
